import { useEffect, useRef } from 'react';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';

interface MapProps {
  center?: google.maps.LatLngLiteral;
  zoom?: number;
  markers?: google.maps.MarkerOptions[];
  onMarkerClick?: (marker: google.maps.Marker) => void;
}

export function Map({ 
  center = { lat: 45.4215, lng: -75.6972 }, 
  zoom = 12, 
  markers = [],
  onMarkerClick 
}: MapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const { isLoaded, error } = useGoogleMaps();

  useEffect(() => {
    if (!isLoaded || !mapRef.current) {
      console.log('Map not ready yet:', { isLoaded, hasRef: !!mapRef.current });
      return;
    }

    try {
      console.log('Initializing map...');
      // Initialize map
      if (!mapInstanceRef.current) {
        const mapOptions: google.maps.MapOptions = {
          center,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          styles: [
            {
              featureType: 'all',
              elementType: 'geometry',
              stylers: [{ color: '#f5f5f5' }]
            },
            {
              featureType: 'water',
              elementType: 'geometry',
              stylers: [{ color: '#e9ecef' }]
            },
            {
              featureType: 'road',
              elementType: 'geometry',
              stylers: [{ color: '#ffffff' }]
            },
            {
              featureType: 'road',
              elementType: 'geometry.stroke',
              stylers: [{ color: '#e9ecef' }]
            },
            {
              featureType: 'road.highway',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'road',
              elementType: 'labels.icon',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'road',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#6b7280' }]
            },
            {
              featureType: 'road',
              elementType: 'labels.text.stroke',
              stylers: [{ color: '#ffffff' }]
            },
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.business',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.place_of_worship',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.school',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.medical',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.park',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'transit',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'transit.line',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'transit.station',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'landscape',
              elementType: 'geometry',
              stylers: [{ color: '#f9fafb' }]
            }
          ]
        };
        mapInstanceRef.current = new google.maps.Map(mapRef.current, mapOptions);
        console.log('Map initialized successfully');
      }

      // Initialize info window
      if (!infoWindowRef.current) {
        infoWindowRef.current = new google.maps.InfoWindow();
      }

      // Clear existing markers
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];

      // Add new markers
      if (mapInstanceRef.current) {
        markers.forEach(markerOptions => {
          const marker = new google.maps.Marker({
            ...markerOptions,
            map: mapInstanceRef.current,
          });

          // Add click listener to marker
          marker.addListener('click', () => {
            if (infoWindowRef.current) {
              infoWindowRef.current.close();
            }
            if (onMarkerClick) {
              onMarkerClick(marker);
            }
          });

          markersRef.current.push(marker);
        });
      }
    } catch (err) {
      console.error('Error initializing map:', err);
    }

    // Cleanup
    return () => {
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      infoWindowRef.current = null;
    };
  }, [center, zoom, markers, isLoaded, onMarkerClick]);

  if (error) {
    return (
      <div className="w-full h-[600px] rounded-lg border flex items-center justify-center">
        <p className="text-red-500">Error: {error}</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-[600px] rounded-lg border flex items-center justify-center">
        <p className="text-muted-foreground">Loading map...</p>
      </div>
    );
  }

  return (
    <div 
      ref={mapRef} 
      className="w-full h-[600px] rounded-lg border"
    />
  );
} 